/**
 * ExpenseSection — dépenses du client avec photo du reçu (Batch #14.243) + lecture automatique IA.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Image as ImageIcon, Loader2, Plus, Receipt, Trash2, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders } from "../helpers.js";
import { useReceiptScan, ScanBanner } from "@/components/shared/ReceiptScan";
import { Field, SectionCard, TaxToggle, PortalReceiptThumb, fmtCAD, inputCls, isDemoResponse, taxesFromTotal, today, addBtnCls, formActionsCls, cancelBtnCls, saveBtnCls, deleteBtnCls } from "./_shared";

const EMPTY = { date: today(), category: "", vendor: "", description: "", total: "", taxed: true, unsure: false, tps: "", tvq: "" };

function ExpenseForm({ categories, rates, primaryColor, onDone, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, category: categories[0] || "Autre" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const { scan, scanning, result, clear } = useReceiptScan(`${API}/portal/entrepreneur/expenses/scan`, portalHeaders);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  // Photo choisie → lecture IA → champs pré-remplis (taxes lues = saisies, sinon calcul auto)
  const pickFile = async (file) => {
    setFile(file);
    const r = await scan(file);
    if (!r) return;
    const hasTax = r.tps != null || r.tvq != null;
    setF((x) => ({
      ...x,
      date: r.date || x.date, vendor: r.vendor || x.vendor, description: r.description || x.description,
      category: categories.includes(r.category) ? r.category : x.category,
      total: r.total != null ? String(r.total) : x.total,
      unsure: false, taxed: hasTax || Number(r.total) > 0,
      ...(hasTax ? { tps: String(r.tps ?? 0), tvq: String(r.tvq ?? 0) } : (r.total != null ? taxesFromTotal(r.total, rates.tps, rates.tvq) : {})),
    }));
  };
  const setTotal = (v) => setF((x) => ({ ...x, total: v, ...(x.taxed ? taxesFromTotal(v, rates.tps, rates.tvq) : {}) }));
  const setTaxed = (on) => setF((x) => ({ ...x, taxed: on, ...(on ? taxesFromTotal(x.total, rates.tps, rates.tvq) : { tps: 0, tvq: 0 }) }));

  const submit = async () => {
    if (!f.date || !(Number(f.total) > 0)) { toast.error("Date et montant requis"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("date", f.date); fd.append("category", f.category); fd.append("vendor", f.vendor);
      fd.append("description", f.description); fd.append("total", String(Number(f.total)));
      // CR-002 : « pas certain » → aucun montant de taxes envoyé → le cabinet qualifie l'entrée
      if (!f.unsure) { fd.append("tps", String(f.taxed ? Number(f.tps) || 0 : 0)); fd.append("tvq", String(f.taxed ? Number(f.tvq) || 0 : 0)); }
      if (file) fd.append("file", file);
      const res = await axios.post(`${API}/portal/entrepreneur/expenses`, fd, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Dépense enregistrée");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 rounded-2xl p-4 space-y-3 bg-gray-50/60" style={{ borderColor: primaryColor }} data-testid="ent-portal-expense-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Date *"><input type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="ent-portal-exp-date" /></Field>
        <Field label="Catégorie *">
          <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="ent-portal-exp-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Fournisseur / magasin"><input className={inputCls} value={f.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="Ex. : Bureau en gros" data-testid="ent-portal-exp-vendor" /></Field>
        <Field label="Montant total payé *"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.total} onChange={(e) => setTotal(e.target.value)} placeholder="0,00" data-testid="ent-portal-exp-total" /></Field>
        <div className="sm:col-span-2 space-y-2">
          {!f.unsure && <TaxToggle checked={f.taxed} onChange={setTaxed} label="Taxes (TPS/TVQ) incluses sur le reçu" testId="ent-portal-exp-taxed" />}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 rounded" checked={f.unsure} onChange={(e) => set("unsure", e.target.checked)} data-testid="ent-portal-exp-unsure" />
            Je ne suis pas certain des taxes — le cabinet vérifiera
          </label>
        </div>
        {f.taxed && !f.unsure && (
          <>
            <Field label="TPS ($)"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.tps} onChange={(e) => set("tps", e.target.value)} data-testid="ent-portal-exp-tps" /></Field>
            <Field label="TVQ ($)"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.tvq} onChange={(e) => set("tvq", e.target.value)} data-testid="ent-portal-exp-tvq" /></Field>
          </>
        )}
        <Field label="Commentaire" className="sm:col-span-2"><input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Ex. : cartouches d'encre" data-testid="ent-portal-exp-description" /></Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={() => camRef.current?.click()} className="border-2 border-dashed rounded-xl p-3 text-center bg-white hover:bg-gray-50 transition" data-testid="ent-portal-exp-camera-btn">
          <Camera className="w-6 h-6 mx-auto text-gray-400 mb-1" />
          <span className="text-xs font-medium text-gray-700">Prendre une photo</span>
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} className="border-2 border-dashed rounded-xl p-3 text-center bg-white hover:bg-gray-50 transition" data-testid="ent-portal-exp-file-btn">
          <ImageIcon className="w-6 h-6 mx-auto text-gray-400 mb-1" />
          <span className="text-xs font-medium text-gray-700">Choisir une image / PDF</span>
        </button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} />
        <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="ent-portal-exp-file-input" />
      </div>
      <ScanBanner scanning={scanning} result={result} onClear={clear} testId="ent-portal-exp-scan" />
      {file && (
        <div className="flex items-center gap-2 text-xs text-gray-700 bg-white border rounded-lg px-3 py-2" data-testid="ent-portal-exp-file-name">
          <Receipt className="w-4 h-4 text-emerald-600" /><span className="truncate flex-1">{file.name}</span>
          <button type="button" onClick={() => { setFile(null); clear(); }} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className={formActionsCls}>
        <button type="button" onClick={onCancel} className={cancelBtnCls}>Annuler</button>
        <button type="button" onClick={submit} disabled={saving || scanning} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-exp-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer la dépense
        </button>
      </div>
    </div>
  );
}

export default function ExpenseSection({ data, primaryColor, onChanged }) {
  const [adding, setAdding] = useState(false);
  const rates = { tps: data.tps_rate, tvq: data.tvq_rate };
  const remove = async (e) => {
    if (!window.confirm(`Supprimer cette dépense de ${fmtCAD(e.total)} ?`)) return;
    try {
      const res = await axios.delete(`${API}/portal/entrepreneur/expenses/${e.id}`, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Dépense supprimée");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <SectionCard title={`Mes dépenses ${data.year}`} icon={Receipt} primaryColor={primaryColor} testId="ent-portal-expenses"
      action={!adding && (
        <button onClick={() => setAdding(true)} className={addBtnCls} style={{ backgroundColor: primaryColor }} data-testid="ent-portal-exp-add-btn">
          <Plus className="w-4 h-4" /> Ajouter une dépense
        </button>
      )}>
      {adding && <div className="mb-4"><ExpenseForm categories={data.categories} rates={rates} primaryColor={primaryColor} onDone={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} /></div>}
      {data.expenses.length === 0 ? (
        <p className="text-sm text-gray-400 italic text-center py-6">Aucune dépense pour {data.year}. Photographiez vos reçus au fur et à mesure !</p>
      ) : (
        <ul className="divide-y" data-testid="ent-portal-exp-list">
          {data.expenses.map((e) => (
            <li key={e.id} className="py-3 flex items-center gap-3" data-testid={`ent-portal-exp-row-${e.id}`}>
              <PortalReceiptThumb url={e.receipt_url} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{e.category}{e.vendor ? ` · ${e.vendor}` : ""}</div>
                <div className="text-xs text-gray-500">{e.date}{e.description ? ` · ${e.description}` : ""}{e.source !== "portal" ? " · saisi par votre gestionnaire" : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900 font-mono">{fmtCAD(e.total)}</div>
                {(e.tps > 0 || e.tvq > 0) && <div className="text-[10px] text-gray-400">TPS {fmtCAD(e.tps)} · TVQ {fmtCAD(e.tvq)}</div>}
                {e.tax_status === "unknown" && <div className="text-[10px] text-amber-600 font-medium" data-testid={`ent-portal-exp-unknown-${e.id}`}>Taxes à vérifier par le cabinet</div>}
              </div>
              {e.source === "portal" && (
                <button onClick={() => remove(e)} className={deleteBtnCls} title="Supprimer" aria-label="Supprimer la dépense" data-testid={`ent-portal-exp-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
